import { z } from 'zod';
import type { Currency, Money, MoneyDTO } from './types.js';

const currencies = new Set(Intl.supportedValuesOf('currency'));
// ECMA-402's list omits ISO 4217 fund units. Keep their defined minor-unit
// precision explicit instead of accepting arbitrary three-letter currency codes.
const fundExponents: Readonly<Record<string, number>> = {
  BOV: 2, CHE: 2, CHW: 2, CLF: 4, COU: 2, MXV: 2, USN: 2, UYI: 0, UYW: 4,
};

const exponents = new Map<string, number>();
export function currencyExponent(currency: Currency): number {
  const fundExponent = Object.hasOwn(fundExponents, currency) ? fundExponents[currency] : undefined;
  if (fundExponent !== undefined) return fundExponent;
  if (!currencies.has(currency)) throw new Error(`Unsupported currency: ${currency}`);
  // Building an Intl.NumberFormat per call made this the hottest line in planning; the answer never changes per currency.
  const known = exponents.get(currency);
  if (known !== undefined) return known;
  const exponent = new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  if (exponent === undefined || !Number.isInteger(exponent) || exponent < 0) {
    throw new Error(`Missing currency precision: ${currency}`);
  }
  exponents.set(currency, exponent);
  return exponent;
}

export function parseMoney(decimal: string, currency: Currency): Money {
  const exponent = currencyExponent(currency);
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(decimal);
  if (!match) throw new Error('Money must be an unambiguous decimal string');
  const fraction = match[3] ?? '';
  if (fraction.length > exponent) throw new Error(`Invalid precision for ${currency}`);
  const magnitude = BigInt(match[2]!) * 10n ** BigInt(exponent)
    + BigInt(fraction.padEnd(exponent, '0') || '0');
  return { minor: match[1] === '-' ? -magnitude : magnitude, currency };
}

export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw new Error('Cannot add different currencies');
  return { minor: a.minor + b.minor, currency: a.currency };
}

export function moneyToDTO(money: Money): MoneyDTO {
  currencyExponent(money.currency);
  return { minor: money.minor.toString(), currency: money.currency };
}

const dtoSchema = z.object({ minor: z.string().regex(/^-?(0|[1-9]\d*)$/), currency: z.string() }).strict();

export function moneyFromDTO(input: unknown): Money {
  const parsed = dtoSchema.parse(input);
  currencyExponent(parsed.currency);
  return { minor: BigInt(parsed.minor), currency: parsed.currency };
}
